import { getQuery, H3Event, sendRedirect } from 'h3';
import { randomUUID } from 'uncrypto';
import { OAuthAuthenticationService } from '../services/oauth-authentication.service';
import { AuthSessionData } from '../types/auth-session.types';
import { AuthRoute } from '../types/auth.types';
import { inject } from '@analog-tools/inject';
import { updateSession } from '@analog-tools/session';
import { sanitizeRedirectUrl } from '../utils/sanitizeRedirectUrl';
import { deriveCodeChallenge, generateCodeVerifier } from '../utils/pkce';

const route: AuthRoute = {
  path: 'login',
  handler: async (event: H3Event) => {
    const authService = inject(OAuthAuthenticationService);

    // Initialize session
    await authService.initSession(event);

    // Generate CSRF state, PKCE verifier/challenge, and an OIDC nonce.
    const state = randomUUID();
    const nonce = randomUUID();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await deriveCodeChallenge(codeVerifier);

    // Persist the values the callback needs to verify the response.
    await updateSession<AuthSessionData>(event, (currentSession) => ({
      ...currentSession,
      state,
      nonce,
      codeVerifier,
    }));

    // Get redirect URL from query parameters
    const query = getQuery(event);
    const redirectUri = query['redirect_uri'] as string;

    const sanitizedTargetPath = redirectUri
      ? sanitizeRedirectUrl(redirectUri)
      : '/';

    await updateSession<AuthSessionData>(event, (currentSession) => ({
      ...currentSession,
      redirectUrl:
        sanitizedTargetPath !== '/' ? sanitizedTargetPath : undefined,
    }));

    // Get authorization URL
    const authUrl = await authService.getAuthorizationUrl({
      state,
      codeChallenge,
      nonce,
    });

    // Redirect to OAuth provider
    return sendRedirect(event, authUrl);
  },
};

export default route;
